<main id="main-content" class="assistant" aria-labelledby="assistant-title">
  <h1 id="assistant-title" class="sr-only">Panorama AI</h1>

  <button
    type="button"
    class="assistant__leave"
    rds-text-button
    size="m"
    (click)="leave()"
  >
    <rds-icon namespace="outlined" icon="arrow_left" />
    Leave Panorama AI
  </button>

  <!-- The transcript scrolls, so it must be keyboard-focusable or a keyboard
       user cannot scroll back through the conversation (AXE rule
       scrollable-region-focusable). The log role already implies a polite live
       region, so no explicit live setting is needed here, and the in-flight
       bubble below is hidden from it so each streamed token does not interrupt
       the previous one. -->
  <!-- `inexpOfferingLinks` is one delegated listener for every offering link in
       every answer below: anchors arrive through `[innerHTML]`, which Angular
       never compiles, so they cannot carry a `routerLink` of their own. -->
  <div
    #transcript
    tabindex="0"
    class="assistant__transcript"
    role="log"
    aria-label="Conversation with Panorama AI"
    inexpOfferingLinks="assistant transcript"
  >
    @if (isGreeting()) {
      <div class="assistant__turn assistant__turn--assistant">
        <h2 class="assistant__label">
          <inexp-sparkle-icon />
          Panorama AI
        </h2>
        <!-- Same wrapper class as a streamed answer, so the greeting is set in
             the same type rather than in whatever the page inherits. -->
        <div class="assistant__bubble inexp-markdown">
          <p>
            Hello! I'm the Panorama AI assistant. I can help you explore Roche's
            IT catalog, compare offerings, check your eligibility, and guide you
            through the request process.
          </p>
          <p>What would you like to find today?</p>
        </div>
      </div>
    }

    @for (message of messages(); track $index; let turnIndex = $index) {
      @if (message.role === 'user') {
        <div class="assistant__turn assistant__turn--user">
          <!-- Assistant turns carry a visible "Panorama AI" label; without this
               the user's own turns are told apart by colour and alignment only. -->
          <span class="sr-only">You said:</span>
          <p class="assistant__pill">{{ message.content }}</p>
        </div>
      } @else {
        <div class="assistant__turn assistant__turn--assistant">
          <h2 class="assistant__label">
            <inexp-sparkle-icon />
            Panorama AI
          </h2>
          @let answer = answerPerMessage()[turnIndex];
          <div class="assistant__bubble">
            <!-- The prose with any call to action lifted out of it — see
                 `assistant-cta.util`. With the flag off this is the answer
                 exactly as written, link and all.

                 Guarded because an answer can be nothing BUT a call to action,
                 which is the shape this story most expects: the model writes
                 one line, it is lifted, and an unguarded block would leave an
                 empty element above the control. -->
            @if (answer?.body) {
              <div
                class="inexp-markdown"
                [innerHTML]="
                  answer?.body | markdown: offeringLinksPerMessage()[turnIndex]
                "
              ></div>
            }

            <!-- The agent's own suggestions, spent as the reader's next turn.
                 The offering itself is reached through the links in the answer
                 text; what is left here is the question the reader has next.

                 Under the newest answer only. No `!isStreaming()` beside that:
                 `send()` empties the row as the next turn leaves, so there is
                 never a moment where suggestions exist and an answer is in
                 flight, and a guard for it would be a branch no test could
                 reach.

                 Each chip carries the whole question, so the visible text IS
                 the accessible name and no `aria-label` is needed (2.5.3 Label
                 in Name). With one row on the page the list's own label is
                 enough — the earlier per-chip naming existed because four
                 opaque labels repeated under every answer.

                 These sit inside the transcript's `role="log"`, so inserting
                 them is announced as prose appended to the answer. `aria-live`
                 cannot carve a quiet hole out of an ancestor live region — an
                 `off` element is not a live region, so the announcement walks
                 past it to the log. Narrowing the log itself is the real fix and
                 is a change to the transcript's own announcement design, logged
                 as a deviation rather than smuggled in here. -->
            <!-- The calls to action share this row, and unlike the questions
                 they stay on every answer that has one: each was lifted out of
                 that answer's own text, so dropping it from an older turn would
                 delete something the reader could see a moment ago. -->
            <ng-container *inexpFeatureFlag="ACTIONS_FLAG">
              @let showChips =
                turnIndex === lastAssistantIndex() && followUps().length;
              @if (answer?.actions?.length || showChips) {
                <div class="assistant__next">
                  @for (cta of answer?.actions; track cta.url) {
                    <!-- An anchor, not a button. This leaves the app, so middle
                         click, Ctrl+click and "copy link address" all have to
                         keep working, and a screen reader should announce a
                         link rather than a button.

                         It wears the design system's classes by hand because
                         `RdsButtonComponent`'s selector is
                         `button[rds-secondary-button]` and does not match an
                         anchor, while the stylesheet keys purely on these
                         classes — the same mechanism the component itself
                         uses. If RDS renames them this degrades to an
                         unstyled link, not to a broken page. -->
                    <!-- `rds-button-wrappable-label` and
                         `rds-button--with-start-icon` are not decoration: the
                         component applies both dynamically, and hand-rolling
                         the list skips them. Without the first, `.rds-button`
                         is `white-space: nowrap` with a fixed `height`, so a
                         long model-written label runs out of the bubble and
                         gives the transcript a horizontal scrollbar. Without
                         the second the icon loses its size, its `min-width`
                         and the gap before the label. -->
                    <a
                      class="rds-button rds-button--secondary rds-button-m rds-button-wrappable-label rds-button--with-start-icon assistant__cta"
                      [href]="cta.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      (click)="trackCallToAction(cta)"
                    >
                      <rds-icon namespace="outlined" icon="external_link" />
                      {{ cta.label }}
                      <!-- Part of the accessible name on purpose: the control
                           looks like a button and behaves like one, so nothing
                           else warns that it leaves the page. -->
                      <span class="sr-only">(opens in a new tab)</span>
                    </a>
                  }

                  @if (showChips) {
                    <inexp-prompt-chips
                      class="align-start"
                      [prompts]="followUps()"
                      label="Suggested follow-up questions"
                      (promptSelected)="askFollowUp($event)"
                    />
                  }
                </div>
              }
            </ng-container>

            <div class="assistant__actions">
              <button
                rds-icon-button
                size="s"
                type="button"
                [attr.aria-label]="
                  copiedIndex() === $index ? 'Copied' : 'Copy answer'
                "
                (click)="copy(message.content, $index)"
              >
                <rds-icon namespace="outlined" icon="copy" />
              </button>
              <!-- Only offered on the newest answer: regenerating an older one
                   would have to discard every turn after it. -->
              @if ($index === lastAssistantIndex() && !isStreaming()) {
                <button
                  rds-icon-button
                  size="s"
                  type="button"
                  aria-label="Regenerate answer"
                  (click)="regenerate()"
                >
                  <rds-icon namespace="outlined" icon="refresh" />
                </button>
              }
              <!-- Shown only where the question named exactly one offering, so
                   the file is unambiguously about something the user asked
                   for. -->
              <ng-container
                *inexpFeatureFlag="'panorama_inexp_2446_offering_markdown'"
              >
                @if (offeringPerMessage()[$index]; as offering) {
                  <button
                    rds-icon-button
                    size="s"
                    type="button"
                    [attr.aria-label]="
                      'Download the ' + offering.displayName + ' offering as a Markdown file'
                    "
                    (click)="downloadOffering(offering)"
                  >
                    <rds-icon namespace="outlined" icon="download" />
                  </button>
                }
              </ng-container>
            </div>
          </div>
        </div>
      }
    }

    @if (isStreaming() || streamingText()) {
      <div class="assistant__turn assistant__turn--assistant">
        <h2 class="assistant__label">
          <inexp-sparkle-icon />
          Panorama AI
        </h2>
        @if (streamingText()) {
          <!-- Hidden from the log while it fills in: the finished turn is
               announced once when it joins `messages()`.

               `inert`, not `aria-hidden`: an answer can contain links, and a
               focusable element inside an `aria-hidden` subtree is an AXE
               `aria-hidden-focus` violation — tabbing mid-stream would land on
               a link that announces nothing. `inert` removes the subtree from
               the accessibility tree AND the tab order. It also blocks text
               selection, which is the trade: this bubble exists for a few
               seconds and is replaced by a fully selectable turn. -->
          <div class="assistant__bubble" inert>
            <div
              class="inexp-markdown"
              [innerHTML]="streamingText() | markdown"
            ></div>
          </div>
        }
      </div>
    }

    <!-- Readouts are hidden individually, not as a block. Hiding the whole
         container also took the Stop button with it (AXE rule
         aria-hidden-focus), so a screen reader user could tab onto the one
         control that interrupts an answer and hear nothing. The status line
         below already carries what these readouts say. -->
    @if (isStreaming()) {
      <div class="assistant__progress">
        <div class="assistant__progress-row">
          <span aria-hidden="true">{{ activeTool() ?? 'Thinking…' }}</span>
          <button
            rds-icon-button
            size="m"
            type="button"
            aria-label="Stop generating"
            (click)="stop()"
          >
            <rds-icon namespace="outlined" icon="circle_stop" />
          </button>
        </div>
        <rds-progress-bar mode="indeterminate" aria-hidden="true" />
      </div>
    }
  </div>

  <!-- Outside the log: nesting an alert inside a live region produces
       duplicated or dropped announcements. -->
  @if (errorMessage()) {
    <div class="assistant__error" role="alert">
      <rds-icon namespace="outlined" icon="warning_triangle" />
      <span>{{ errorMessage() }}</span>
    </div>
  }

  <!-- One short announcement instead of a token-by-token stream. -->
  <p class="sr-only" role="status">{{ statusMessage() }}</p>

  <form class="assistant__composer" (submit)="$event.preventDefault(); send()">
    <label class="sr-only" for="assistant-input">Message Panorama AI</label>
    <rds-form-field labelPosition="none" class="assistant__field">
      <input
        #composer
        id="assistant-input"
        rdsInput
        type="text"
        placeholder="Ask about an offering…"
        [ngModel]="draft()"
        (ngModelChange)="draft.set($event)"
        [ngModelOptions]="{ standalone: true }"
        [disabled]="isStreaming()"
        autocomplete="off"
      />
    </rds-form-field>

    <button
      type="submit"
      rds-primary-button
      size="l"
      [disabled]="!draft().trim() || isStreaming()"
    >
      Send
    </button>
  </form>
</main>
